/**
 * Active-query store (zustand). Spec §6: the Base screen "holds filters[] + page +
 * pageSize + sort". Sibling components (FilterBuilder, VolunteersTable, SelectionBar,
 * ExportButton, SavedFilters) share this state through the store rather than props.
 *
 * `filters` is the *applied* query — what the table / count / export read from. The
 * FilterBuilder keeps its own local draft and writes here only on "Apply" / "Reset",
 * so we don't hammer the upstream API (197k rows) on every keystroke.
 */

import { create } from "zustand";
import type { FilterClause, FilterEnvelope } from "@/api/client";

export const DEFAULT_PAGE_SIZE = 50;
// mob.er.ru caps server-side page size: 25/50/100 OK, 200 -> HTTP 400. Keep <=100.
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export interface FiltersState {
  /** The applied filter the table / count / export query with. */
  filters: FilterClause[];
  /** 1-based current page. */
  page: number;
  pageSize: number;
  /** Sort string: "field" (asc) | "-field" (desc) | null. */
  sort: string | null;

  /** Replace the whole applied filter list (resets to page 1). */
  setFilters: (filters: FilterClause[]) => void;
  /** Append a single clause (resets to page 1). */
  addClause: (clause: FilterClause) => void;
  /** Remove the clause at `index` (resets to page 1). */
  removeClause: (index: number) => void;
  /** Clear all clauses (resets to page 1). */
  clearFilters: () => void;

  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  /** Set sort directly ("field" | "-field" | null); resets to page 1. */
  setSort: (sort: string | null) => void;

  /** Load a saved-filter envelope into the applied filter (resets to page 1). */
  applyEnvelope: (envelope: FilterEnvelope) => void;
}

export const useFilters = create<FiltersState>((set) => ({
  filters: [],
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: null,

  setFilters: (filters) => set({ filters, page: 1 }),
  addClause: (clause) => set((s) => ({ filters: [...s.filters, clause], page: 1 })),
  removeClause: (index) =>
    set((s) => ({ filters: s.filters.filter((_, i) => i !== index), page: 1 })),
  clearFilters: () => set({ filters: [], page: 1 }),

  setPage: (page) => set({ page: Math.max(1, page) }),
  setPageSize: (pageSize) => set({ pageSize, page: 1 }),
  setSort: (sort) => set({ sort, page: 1 }),

  applyEnvelope: (envelope) => set({ filters: envelope.filters ?? [], page: 1 }),
}));

/** Parse a sort string ("field" | "-field" | null) into a column + direction. */
export function parseSort(sort: string | null): { field: string | null; desc: boolean } {
  if (!sort) return { field: null, desc: false };
  if (sort.startsWith("-")) return { field: sort.slice(1), desc: true };
  return { field: sort, desc: false };
}

/** Cycle a column's sort: none -> asc -> desc -> none. Returns the next sort string. */
export function cycleSort(current: string | null, field: string): string | null {
  if (current === field) return `-${field}`;
  if (current === `-${field}`) return null;
  return field;
}
